import { useCallback, useEffect, useMemo, useState } from 'react'
import { Cake, Check, ChevronDown, ChevronRight, ChevronUp, Clock, Gift, LocateFixed, LogIn, MapPin, Medal, Navigation, Phone, QrCode, Search, Sparkles, Ticket, Trophy, UserRound, X } from 'lucide-react'
import { ApiFout, foutTekst, vraag } from '../lib/api'
import { datum, datumTijd, geleden, getal, punten } from '../lib/opmaak'
import { bepaalPlek, uitleg, type LocatieFout } from '../lib/locatie'
import { afstand, appleRoute, googleRoute, isApple } from '../lib/route'
import { useInstalleren } from '../lib/installeren'
import { klikNaar } from '../lib/router'
import type { KlantOverzicht, Openbaar, Rang, Vestiging } from '../lib/typen'
import { DAGEN, DAGNAAM, MAANDNAAM, amsterdam, euro, normaliseerCode, openStand, type OpenStand } from '../../supabase/functions/_gedeeld/regels.ts'
import { Installeren } from '../onderdelen/Installeren'
import { KaartQr } from '../onderdelen/KaartQr'
import { Lade } from '../onderdelen/Lade'
import { Logo } from '../onderdelen/Logo'
import { Scanner } from '../onderdelen/Scanner'

const ONTHOUD = 'tp-mijn-code'
const lees = () => { try { return localStorage.getItem(ONTHOUD) } catch { return null } }
const bewaar = (c: string | null) => { try { if (c) localStorage.setItem(ONTHOUD, c); else localStorage.removeItem(ONTHOUD) } catch { /* privévenster */ } }

export function Klant({ data }: { data: Openbaar | null }) {
  const [klant, zetKlant] = useState<KlantOverzicht | null>(null)
  const [toonQr, zetToonQr] = useState(false)
  const [laden, zetLaden] = useState(false)
  const [fout, zetFout] = useState<string | null>(null)

  const zoek = useCallback(async (invoer: string, stil = false) => {
    const code = normaliseerCode(invoer)
    if (!code) { zetFout('Dit lijkt geen code van een kaart. Typ de letters en cijfers die bij de QR-code staan.'); return }
    zetLaden(true); zetFout(null)
    try {
      const r = await vraag<{ klant: KlantOverzicht }>('saldo', { code })
      zetKlant(r.klant)
      bewaar(r.klant.code)
    } catch (e) {
      if (stil && e instanceof ApiFout && e.status === 404) bewaar(null)
      else zetFout(foutTekst(e))
    } finally { zetLaden(false) }
  }, [])

  useEffect(() => {
    /* De link uit een mail (/?code=...) opent meteen het eigen saldo; daarna
       halen we hem uit de adresbalk, zodat hij niet in een gedeelde link blijft. */
    const uitLink = new URLSearchParams(location.search).get('code')
    if (uitLink) { history.replaceState(null, '', '/'); zoek(uitLink); return }
    const c = lees()
    if (c) zoek(c, true)
  }, [zoek])

  return (
    <div className="klant">
      <header className="klant-kop">
        <a href="/" className="merk" onClick={klikNaar('/')}><Logo vlak /><b>TW1 <span>Club</span></b></a>
        <span className="vul" />
        {klant && (
          <button className="knop geel klein-knop" onClick={() => zetToonQr(true)} aria-label="Toon de QR-code van mijn kaart">
            <QrCode size={18} /><span>Mijn kaart</span>
          </button>
        )}
        <a href="/beheer" className="knop stil-knop klein-knop" onClick={klikNaar('/beheer')} aria-label="Inloggen voor medewerkers">
          <LogIn size={18} />{!klant && <span className="klein verberg-smal">Medewerkers</span>}
        </a>
      </header>

      <main className="klant-lijf">
        <div className="klant-links lijst" style={{ gap: 18 }}>
          <Vlakbij data={data} klant={klant} />
          {klant
            ? <MijnPunten klant={klant} data={data} vernieuw={() => zoek(klant.code)} zet={zetKlant}
                vergeet={() => { bewaar(null); zetKlant(null) }} />
            : <Held laden={laden} fout={fout} zoek={zoek} />}
          {!klant && data && <HoeWerktHet data={data} />}
          <AlsApp />
        </div>
        <Locaties data={data} />
      </main>

      <Lade titel="Mijn kaart" open={toonQr && !!klant} sluit={() => zetToonQr(false)} kinderen={klant && <KaartQr code={klant.code} />} />

      <footer className="voet">
        TW1 Club van <a href="https://www.truckwash1group.nl" target="_blank" rel="noreferrer">Truckwash 1 Group</a>
      </footer>
    </div>
  )
}

/* ------------------------------------------------------------------ */

function Held({ laden, fout, zoek }: { laden: boolean; fout: string | null; zoek: (c: string) => void }) {
  const [code, zetCode] = useState('')
  const [camera, zetCamera] = useState(false)
  return (
    <section className="held">
      <h1>Spaar bij elke wasbeurt <span>TW1 Club</span></h1>
      <p>Bekijk je punten met de code op je kaart, of scan de QR-code met je camera.</p>
      <form className="zoek" onSubmit={(e) => { e.preventDefault(); zoek(code) }}>
        <label className="sr" htmlFor="code">Code van je kaart</label>
        <input id="code" className="code-invoer" value={code} onChange={(e) => zetCode(e.target.value)}
          placeholder="Code van je kaart" autoCapitalize="characters" autoComplete="off" autoCorrect="off" spellCheck={false}
          inputMode="text" maxLength={30} enterKeyHint="search" />
        <div className="knoppen">
          <button className="knop geel" disabled={laden || code.trim().length < 6}>
            {laden ? 'Even kijken…' : <><Search size={19} />Bekijk mijn punten</>}
          </button>
          <button type="button" className="knop rond" onClick={() => zetCamera(true)} aria-label="Scan je kaart met de camera">
            <QrCode size={22} />
          </button>
        </div>
        {fout && <div className="melding fout">{fout}</div>}
      </form>
      <Lade titel="Scan je kaart" open={camera} sluit={() => zetCamera(false)} kinderen={
        <div className="lijst">
          {camera && <Scanner gevonden={(t) => { zetCamera(false); zetCode(normaliseerCode(t) ?? t); zoek(t) }} />}
          <p className="stil klein" style={{ textAlign: 'center' }}>Houd de QR-code van je kaart in het kader.</p>
        </div>
      } />
    </section>
  )
}

/** Ingeklapt: drie stuks; wat je nu al kunt halen (de duurste 2) aangevuld met de eerstvolgende. */
function zichtbareBeloningen<T extends { punten: number; uitverkocht?: boolean }>(lijst: T[], saldo: number): T[] {
  const te = lijst.filter((b) => !b.uitverkocht)
  const kan = te.filter((b) => b.punten <= saldo).slice(-2)
  return [...kan, ...te.filter((b) => b.punten > saldo).slice(0, 3 - kan.length)]
}

/** Jouw rang: op het aantal wasbeurten, met de voortgang naar de volgende. */
function RangKaart({ rang }: { rang: Rang }) {
  const v = rang.volgende
  const deel = v ? Math.min(1, (rang.wasbeurten - v.vanaf) / Math.max(1, v.drempel - v.vanaf)) : 1
  return (
    <section className="kaart vol rang-kaart" style={{ ['--rang' as string]: rang.kleur ?? 'var(--brand)' }}>
      <div className="rij" style={{ flexWrap: 'nowrap', gap: 14 }}>
        {rang.afbeelding ? <img className="rang-badge" src={rang.afbeelding} alt="" /> : <span className="rang-badge leeg"><Medal size={26} /></span>}
        <div className="vul">
          <span className="saldo-label">Jouw rang</span>
          <b style={{ display: 'block', fontSize: 19, letterSpacing: '-.01em' }}>{rang.naam}</b>
          <span className="stil klein">{getal(rang.wasbeurten)} wasbeurten</span>
        </div>
      </div>
      {v ? (
        <div style={{ marginTop: 14 }}>
          <div className="rij" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
            <span className="klein">Nog <b>{getal(v.nog)}</b> wasbeurten tot <b>{v.naam}</b></span>
          </div>
          <div className="voortgang"><i style={{ width: `${Math.max(4, Math.round(deel * 100))}%` }} /></div>
        </div>
      ) : <p className="klein" style={{ marginTop: 12 }}>De hoogste rang. Respect!</p>}
      {rang.voordelen.length > 0 && (
        <div className="chips" style={{ marginTop: 12, marginBottom: 0, flexWrap: 'wrap' }}>
          {rang.voordelen.map((x) => <span key={x} className="badge geel"><Check size={13} />{x}</span>)}
        </div>
      )}
    </section>
  )
}

/** Kaartje "Truckpoints als app", alleen als het op dit toestel kan. */
function AlsApp() {
  const { soort } = useInstalleren()
  if (soort === 'geen') return null
  return (
    <section className="kaart vol">
      <div className="rij" style={{ flexWrap: 'nowrap', alignItems: 'flex-start', gap: 14, marginBottom: 12 }}>
        <img src="/icoon-192.png" alt="" width={48} height={48} style={{ borderRadius: 12, flex: 'none' }} />
        <div><b>TW1 Club als app</b><p className="stil klein" style={{ marginTop: 2 }}>Met één tik je punten, beloningen en alle vestigingen bij de hand, zonder adresbalk.</p></div>
      </div>
      <Installeren klasse="knop geel breed" />
    </section>
  )
}

function HoeWerktHet({ data }: { data: Openbaar }) {
  const r = data.regels
  const eerste = data.beloningen[0]
  const stappen = [
    { i: <QrCode size={22} />, t: 'Scan je kaart', s: 'Op de tablet bij de kassa, bij elke wasbeurt.' },
    { i: <Sparkles size={22} />, t: `${punten(r.puntenPerWasbeurt)} per wasbeurt`, s: `Op elke vestiging. Op dezelfde vestiging één keer per ${r.cooldownMinuten} minuten.` },
    { i: <Gift size={22} />, t: eerste ? `${eerste.punten} punten = ${eerste.naam}` : 'Wissel je punten in', s: 'Kies je beloning op de tablet; een medewerker bevestigt.' },
  ]
  return (
    <section className="kaart vol">
      <div className="sectie-kop" style={{ marginTop: 0 }}><h2>Zo werkt het</h2></div>
      <ul className="tijdlijn">
        {stappen.map((s) => (
          <li key={s.t}><span className="beloning-icoon">{s.i}</span><span><b>{s.t}</b><br /><span className="stil klein">{s.s}</span></span></li>
        ))}
      </ul>
    </section>
  )
}

/* ------------------------------------------------------------------ */

function MijnPunten({ klant, data, vergeet, zet, vernieuw }: {
  klant: KlantOverzicht; data: Openbaar | null; vergeet: () => void; zet: (k: KlantOverzicht) => void; vernieuw: () => void
}) {
  const volgende = klant.beloningen.find((b) => b.punten > klant.saldo)
  const vorigeGrens = [...klant.beloningen].reverse().find((b) => b.punten <= klant.saldo)?.punten ?? 0
  const bereikt = klant.beloningen.filter((b) => b.punten <= klant.saldo)
  const deel = volgende ? Math.min(1, (klant.saldo - vorigeGrens) / Math.max(1, volgende.punten - vorigeGrens)) : 1
  const waarde = data ? klant.saldo * data.regels.waardePerPuntEuro : null
  const [gegevens, zetGegevens] = useState(false)
  const [alleBeloningen, zetAlleBeloningen] = useState(false)
  const c = klant.chauffeur

  return (
    <>
      <section className="kaart saldo-kaart" aria-live="polite">
        <div className="saldo-top">
          <div className="vul">
            <div className="saldo-label">Jouw saldo</div>
            <div className="saldo-getal">{getal(klant.saldo)}</div>
            <div className="stil klein" style={{ marginTop: 4 }}>
              punten{waarde !== null && waarde > 0 ? ` · ${euro(waarde)} waard` : ''}
            </div>
          </div>
          <div style={{ display: 'grid', gap: 8, justifyItems: 'end' }}>
            <span className="badge geel code-pil">{klant.code}</span>
            <button className="knop stil-knop klein-knop" onClick={vergeet}><X size={16} />Andere kaart</button>
          </div>
        </div>
        {volgende ? (
          <div>
            <div className="rij" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
              <b style={{ fontSize: 15 }}>Nog {punten(volgende.punten - klant.saldo)} tot {volgende.naam}</b>
              <span className="stil klein cijfer">{klant.saldo}/{volgende.punten}</span>
            </div>
            <div className="voortgang"><i style={{ width: `${Math.round(deel * 100)}%` }} /></div>
          </div>
        ) : klant.beloningen.length ? (
          <div className="melding ok"><Gift size={19} /><span>Je hebt genoeg punten voor elke beloning. Wissel in bij de kassa.</span></div>
        ) : null}
        {bereikt.length > 0 && volgende && (
          <div className="melding ok"><Gift size={19} /><span>Je kunt nu <b>{bereikt[bereikt.length - 1]?.naam}</b> inwisselen. Scan je kaart op de tablet bij de kassa.</span></div>
        )}
      </section>

      {klant.rang && <RangKaart rang={klant.rang} />}

      {klant.vouchers.length > 0 && (
        <section className="kaart vol">
          <div className="sectie-kop" style={{ marginTop: 0 }}><h2>Mijn vouchers</h2><span className="badge geel">{klant.vouchers.length}</span></div>
          {klant.vouchers.map((v) => (
            <div key={v.id} className="beloning-rij bereikt">
              {v.afbeelding ? <img className="beloning-foto" src={v.afbeelding} alt="" loading="lazy" /> : <span className="beloning-icoon"><Ticket size={22} /></span>}
              <span className="vul"><b>{v.naam}</b><br />
                <span className="stil klein">{v.omschrijving ? `${v.omschrijving} · ` : ''}{v.verloopt ? `geldig t/m ${datum(v.verloopt)}` : 'geen einddatum'}</span></span>
            </div>
          ))}
          <p className="stil klein" style={{ marginTop: 10 }}>Gebruiken: scan je kaart op de tablet en kies de voucher; een medewerker bevestigt.</p>
        </section>
      )}

      {klant.beloningen.length > 0 && (
        <section className="kaart vol">
          <div className="sectie-kop" style={{ marginTop: 0 }}><h2>Beloningen</h2><span className="stil klein">{klant.beloningen.length}</span></div>
          {(alleBeloningen ? klant.beloningen : zichtbareBeloningen(klant.beloningen, klant.saldo)).map((b) => (
            <div key={b.id} className={`beloning-rij ${b.punten <= klant.saldo ? 'bereikt' : ''}`}>
              {b.afbeelding ? <img className="beloning-foto" src={b.afbeelding} alt="" loading="lazy" /> : <span className="beloning-icoon"><Gift size={22} /></span>}
              <span className="vul"><b>{b.naam}</b><br /><span className="stil klein">{b.punten} punten{b.type === 'digitaal' ? ' · digitaal' : ''}{b.waar?.length ? ` · alleen bij ${b.waar.join(', ')}` : ''}</span></span>
              {b.uitverkocht ? <span className="badge">Uitverkocht</span> : b.punten <= klant.saldo ? <span className="badge open">Beschikbaar</span> : <span className="stil klein cijfer" style={{ whiteSpace: 'nowrap', flex: 'none' }}>nog {b.punten - klant.saldo}</span>}
            </div>
          ))}
          {klant.beloningen.length > 3 && (
            <button className="knop stil-knop breed" style={{ marginTop: 6 }} onClick={() => zetAlleBeloningen(!alleBeloningen)}>
              {alleBeloningen ? <>Minder tonen <ChevronUp size={18} /></> : <>Alle {klant.beloningen.length} beloningen <ChevronDown size={18} /></>}
            </button>
          )}
        </section>
      )}

      {c.totaal > 0 && (
        <section className="kaart vol">
          <div className="rij" style={{ alignItems: 'flex-start', gap: 14, flexWrap: 'nowrap' }}>
            <span className="beloning-icoon"><Trophy size={22} /></span>
            <div className="vul">
              <b>Chauffeursbeloning</b>
              <p className="stil klein" style={{ marginTop: 2 }}>
                {c.bereikt
                  ? 'Gehaald! Je hebt genoeg verschillende vestigingen bezocht.'
                  : `Was binnen ${c.maanden} maanden op ${c.nodig} van de ${c.totaal} vestigingen. Je teller staat op ${c.bezocht}.`}
              </p>
            </div>
            <b className="cijfer" style={{ fontSize: 20 }}>{c.bezocht}<span className="stil">/{c.nodig}</span></b>
          </div>
          <div className="voortgang" style={{ marginTop: 12 }}><i style={{ width: `${Math.min(100, Math.round((c.bezocht / Math.max(1, c.nodig)) * 100))}%` }} /></div>
        </section>
      )}

      <section className="kaart vol">
        <div className="sectie-kop" style={{ marginTop: 0 }}>
          <h2>Laatste bezoeken</h2>
          <button className="knop stil-knop klein-knop" onClick={vernieuw}>Vernieuwen</button>
        </div>
        {klant.bezoeken.length ? (
          <ul className="tijdlijn">
            {klant.bezoeken.slice(0, 8).map((b) => (
              <li key={b.t}><MapPin size={18} className="stil" /><span className="vul">{b.vestiging}</span><span className="stil klein" title={datumTijd(b.t)}>{geleden(b.t)}</span></li>
            ))}
          </ul>
        ) : <p className="stil">Nog geen bezoeken.</p>}
      </section>

      {klant.verjaardagPunten > 0 && !klant.verjaardag && (
        <button className="melding info" style={{ border: 0, textAlign: 'left', cursor: 'pointer', width: '100%' }} onClick={() => zetGegevens(true)}>
          <Cake size={20} /><span>Vul je verjaardag in en krijg op je verjaardag <b>{klant.verjaardagPunten} punten</b> cadeau.</span>
        </button>
      )}
      <section className="kaart vol">
        <button className="rij knop stil-knop breed" style={{ justifyContent: 'flex-start', padding: 0, flexWrap: 'nowrap' }} onClick={() => zetGegevens(true)}>
          <UserRound size={20} /><span className="vul" style={{ textAlign: 'left' }}>
            <b style={{ color: 'var(--text)' }}>Mijn gegevens</b><br />
            <span className="stil klein">
              {[klant.naam || null,
                klant.verjaardag ? `jarig ${klant.verjaardag.dag} ${MAANDNAAM[klant.verjaardag.maand - 1]}` : null,
                klant.toestemming ? 'mail aan' : 'geen mail'].filter(Boolean).join(' · ')} · tik om te wijzigen
            </span>
          </span><ChevronRight size={20} className="stil" />
        </button>
      </section>
      <Lade titel="Mijn gegevens" open={gegevens} sluit={() => zetGegevens(false)}
        kinderen={<Gegevens klant={klant} klaar={(k) => { zet(k); zetGegevens(false) }} />} />
    </>
  )
}

function Gegevens({ klant, klaar }: { klant: KlantOverzicht; klaar: (k: KlantOverzicht) => void }) {
  const [voornaam, zetVoornaam] = useState(klant.voornaam)
  const [achternaam, zetAchternaam] = useState(klant.achternaam)
  const [dag, zetDag] = useState(klant.verjaardag?.dag ?? 0)
  const [maand, zetMaand] = useState(klant.verjaardag?.maand ?? 0)
  const [email, zetEmail] = useState('')
  const [ja, zetJa] = useState(klant.toestemming || !klant.toestemmingGekozen)
  const [bezig, zetBezig] = useState(false)
  const [fout, zetFout] = useState<string | null>(null)
  const opslaan = async () => {
    zetBezig(true); zetFout(null)
    try {
      const verjaardag = dag && maand ? { dag, maand } : klant.verjaardag && !dag && !maand ? null : undefined
      if ((dag && !maand) || (!dag && maand)) throw new Error('Kies zowel de dag als de maand van je verjaardag.')
      const r = await vraag<{ klant: KlantOverzicht }>('gegevens-opslaan', {
        code: klant.code, voornaam, achternaam, verjaardag, email: email.trim() || undefined, toestemming: ja,
      })
      klaar(r.klant)
    } catch (e) { zetFout(foutTekst(e)) } finally { zetBezig(false) }
  }
  const gewijzigd = !!dag && !!maand && (!klant.verjaardag || klant.verjaardag.dag !== dag || klant.verjaardag.maand !== maand)
  return (
    <form className="formulier" onSubmit={(e) => { e.preventDefault(); opslaan() }}>
      <div className="veld">
        <span>Wat is je naam?</span>
        <div className="twee-kol">
          <input value={voornaam} onChange={(e) => zetVoornaam(e.target.value)} autoComplete="given-name" maxLength={40} placeholder="Voornaam" aria-label="Voornaam" />
          <input value={achternaam} onChange={(e) => zetAchternaam(e.target.value)} autoComplete="family-name" maxLength={40} placeholder="Achternaam (mag leeg)" aria-label="Achternaam" />
        </div>
        <small>In een mail spreken we je aan met je voornaam.</small>
      </div>
      <div className="veld">
        <span>Je verjaardag</span>
        <div className="twee-kol" style={{ gridTemplateColumns: '1fr 1.6fr' }}>
          <select value={dag} onChange={(e) => zetDag(Number(e.target.value))} aria-label="Dag">
            <option value={0}>Dag</option>
            {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
          </select>
          <select value={maand} onChange={(e) => zetMaand(Number(e.target.value))} aria-label="Maand">
            <option value={0}>Maand</option>
            {MAANDNAAM.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <small>
          Alleen dag en maand; je geboortejaar hebben we niet nodig.
          {klant.verjaardagPunten > 0 ? ` Op je verjaardag krijg je ${klant.verjaardagPunten} punten cadeau.` : ''}
          {gewijzigd ? ' Een nieuwe of gewijzigde datum telt pas na 30 dagen.' : ''}
        </small>
      </div>
      <label className="veld">
        <span>E-mailadres</span>
        <input type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => zetEmail(e.target.value)}
          placeholder={klant.email ?? 'jij@voorbeeld.nl'} />
        {klant.email && <small>Leeg laten houdt {klant.email}.</small>}
      </label>
      <label className="vink">
        <input type="checkbox" checked={ja} onChange={(e) => zetJa(e.target.checked)} />
        <span>Ja, Truckwash 1 Group mag mij aanbiedingen en nieuws over TW1 Club mailen (ook een felicitatie op mijn verjaardag). Afmelden kan hier altijd.</span>
      </label>
      {fout && <div className="melding fout">{fout}</div>}
      <button className="knop geel breed" disabled={bezig}>{bezig ? 'Opslaan…' : 'Opslaan'}</button>
    </form>
  )
}

/* ------------------------------------------------------------------ *
 *  Locaties
 * ------------------------------------------------------------------ */

/** Binnen deze afstand verschijnt de balk "Je bent vlakbij". */
const VLAKBIJ_KM = 5

/*
 * Staat de webapp open en mag de browser de locatie al geven (de klant tikte
 * ooit op "Dichtbij mij")? Dan een balk als je binnen 5 km van een vestiging
 * bent. Vraagt nooit zelf om toestemming, en de locatie blijft in de browser:
 * er gaat niets naar onze server.
 */
function Vlakbij({ data, klant }: { data: Openbaar | null; klant: KlantOverzicht | null }) {
  const [plek, zetPlek] = useState<{ lat: number; lon: number } | null>(null)
  const [weg, zetWeg] = useState(() => { try { return sessionStorage.getItem('tp-vlakbij-weg') === '1' } catch { return false } })
  useEffect(() => {
    if (weg || !('geolocation' in navigator) || !navigator.permissions?.query) return
    let stop = false
    let status: PermissionStatus | null = null
    const haal = () => navigator.geolocation.getCurrentPosition(
      (x) => { if (!stop) zetPlek({ lat: x.coords.latitude, lon: x.coords.longitude }) },
      () => { /* geen plek, geen balk */ },
      { enableHighAccuracy: false, maximumAge: 300_000, timeout: 10_000 })
    navigator.permissions.query({ name: 'geolocation' as PermissionName }).then((p) => {
      if (stop) return
      status = p
      if (p.state === 'granted') haal()
      p.onchange = () => { if (p.state === 'granted') haal() }
    }).catch(() => { /* deze browser vertelt het niet: dan niet vragen */ })
    return () => { stop = true; if (status) status.onchange = null }
  }, [weg])
  const dichtste = useMemo(() => {
    if (!data || !plek) return null
    return data.vestigingen
      .filter((v) => v.lat && v.lon)
      .map((v) => ({ v, km: afstand(plek, { lat: v.lat!, lon: v.lon! }) }))
      .sort((a, b) => a.km - b.km)[0] ?? null
  }, [data, plek])
  if (weg || !dichtste || dichtste.km > VLAKBIJ_KM) return null
  const { v, km } = dichtste
  const s = openStand(v.uren, new Date())
  return (
    <div className="vlakbij" role="status">
      <span className="vlakbij-icoon"><MapPin size={20} /></span>
      <span className="vul" style={{ minWidth: 0 }}>
        <b>Je bent vlakbij {v.naam.replace(/^Truckwash\s*\d*\s*/i, '')}</b>
        <span className="klein vlakbij-sub">
          {km < 10 ? km.toFixed(1).replace('.', ',') : Math.round(km)} km · {s.open ? `open tot ${s.tot}` : 'nu dicht'}
          {klant ? ` · je hebt ${punten(klant.saldo)}` : ''}
        </span>
      </span>
      <a className="knop geel klein-knop" href={isApple() ? appleRoute(v) : googleRoute(v)} target="_blank" rel="noreferrer" aria-label="Route"><Navigation size={16} /><span className="vlakbij-route">Route</span></a>
      <button className="knop stil-knop rond" aria-label="Sluiten" onClick={() => {
        zetWeg(true)
        try { sessionStorage.setItem('tp-vlakbij-weg', '1') } catch { /* privévenster */ }
      }}><X size={18} /></button>
    </div>
  )
}

function OpenBadge({ s }: { s: OpenStand }) {
  if (s.onbekend) return <span className="badge">Tijden volgen</span>
  if (s.open) return <span className="badge open"><span className="punt" />Open tot {s.tot}</span>
  const v = s.volgende
  return <span className="badge dicht">Dicht{v ? ` · ${v.vandaag ? 'vandaag' : v.morgen ? 'morgen' : DAGNAAM[v.dag].slice(0, 2)} ${v.van}` : ''}</span>
}

function Locaties({ data }: { data: Openbaar | null }) {
  const [q, zetQ] = useState('')
  const [nuOpen, zetNuOpen] = useState(false)
  const [plek, zetPlek] = useState<{ lat: number; lon: number; naam?: string } | null>(null)
  const [plekFout, zetPlekFout] = useState<string | null>(null)
  const [zoektPlek, zetZoektPlek] = useState(false)
  const [typPlaats, zetTypPlaats] = useState(false)
  const [plaats, zetPlaats] = useState('')
  const [gekozen, kies] = useState<Vestiging | null>(null)
  const [nu, zetNu] = useState(() => new Date())
  useEffect(() => { const t = setInterval(() => zetNu(new Date()), 60_000); return () => clearInterval(t) }, [])

  /* Alleen in de browser: de locatie gaat niet naar onze server en wordt nergens bewaard. */
  const dichtbij = async () => {
    if (plek) { zetPlek(null); return }
    zetPlekFout(null)
    zetZoektPlek(true)
    try {
      zetPlek(await bepaalPlek())
      zetTypPlaats(false)
    } catch (f) {
      zetPlekFout(uitleg(f as LocatieFout))
      zetTypPlaats(true)
    } finally { zetZoektPlek(false) }
  }

  const zoekPlaats = async () => {
    if (plaats.trim().length < 2) return
    zetZoektPlek(true); zetPlekFout(null)
    try {
      const r = await fetch(`/api/plaats?q=${encodeURIComponent(plaats.trim())}`)
      const d = await r.json() as { ok: boolean; naam?: string; lat?: number; lon?: number; fout?: string }
      if (!d.ok || d.lat === undefined || d.lon === undefined) throw new Error(d.fout ?? 'Die plaats vinden we niet.')
      zetPlek({ lat: d.lat, lon: d.lon, naam: d.naam })
      zetTypPlaats(false)
    } catch (e) {
      zetPlekFout(e instanceof Error ? e.message : 'Die plaats vinden we niet.')
    } finally { zetZoektPlek(false) }
  }

  const lijst = useMemo(() => {
    if (!data) return []
    const z = q.trim().toLowerCase()
    return data.vestigingen
      .map((v) => ({ v, s: openStand(v.uren, nu), km: plek && v.lat && v.lon ? afstand(plek, { lat: v.lat, lon: v.lon }) : null }))
      .filter((x) => !z || `${x.v.naam} ${x.v.plaats} ${x.v.adres}`.toLowerCase().includes(z))
      .filter((x) => !nuOpen || x.s.open)
      .sort((a, b) => (a.km !== null && b.km !== null ? a.km - b.km : a.v.naam.localeCompare(b.v.naam)))
  }, [data, q, nuOpen, plek, nu])
  const open = data ? data.vestigingen.filter((v) => openStand(v.uren, nu).open).length : 0

  return (
    <section>
      <div className="sectie-kop">
        <h2>Vestigingen</h2>
        {data && <span className="badge open">{open} nu open</span>}
      </div>
      <div className="filter-balk">
        <label className="sr" htmlFor="zoek-plek">Zoek een vestiging</label>
        <input id="zoek-plek" className="invoer" type="search" value={q} onChange={(e) => zetQ(e.target.value)} placeholder="Zoek op plaats" enterKeyHint="search" />
      </div>
      <div className="chips" role="group" aria-label="Filters">
        <button className="chip" aria-pressed={nuOpen} onClick={() => zetNuOpen(!nuOpen)}><Clock size={16} />Nu open</button>
        <button className="chip" aria-pressed={!!plek && !plek.naam} onClick={() => { if (plek?.naam) zetPlek(null); dichtbij() }} disabled={zoektPlek}>
          <LocateFixed size={16} className={zoektPlek ? 'draai' : ''} />{zoektPlek ? 'Locatie zoeken…' : 'Dichtbij mij'}
        </button>
        <button className="chip" aria-pressed={typPlaats || !!plek?.naam} onClick={() => { if (plek?.naam) { zetPlek(null); zetTypPlaats(false) } else zetTypPlaats(!typPlaats) }}>
          <MapPin size={16} />{plek?.naam ? <>Bij {plaats.trim().toUpperCase()} <X size={14} /></> : 'Postcode'}
        </button>
      </div>
      {plekFout && <div className="melding let" style={{ marginBottom: 10 }}>{plekFout}</div>}
      {typPlaats && (
        <form className="filter-balk" onSubmit={(e) => { e.preventDefault(); zoekPlaats() }}>
          <label className="sr" htmlFor="plaats">Plaats of postcode</label>
          <input id="plaats" className="invoer" value={plaats} onChange={(e) => zetPlaats(e.target.value)} placeholder="Bijv. Breda of 4704 RM"
            autoComplete="postal-code" enterKeyHint="search" />
          <button className="knop geel" disabled={zoektPlek || plaats.trim().length < 2}>Sorteer</button>
        </form>
      )}
      {!data ? <p className="stil">Laden…</p> : (
        <div className="lijst">
          {lijst.map(({ v, s, km }) => (
            <button key={v.id} className="locatie" onClick={() => kies(v)}>
              <h3>{v.naam.replace(/^Truckwash\s*/i, '')}</h3>
              <OpenBadge s={s} />
              <span className="sub">
                <span>{v.adres ? `${v.adres}${v.plaats && v.plaats.toLowerCase() !== v.naam.replace(/^Truckwash\s*/i, '').toLowerCase() ? `, ${v.plaats}` : ''}` : 'Adres volgt'}
                  {km !== null && <span className="cijfer"> · {km < 10 ? km.toFixed(1).replace('.', ',') : Math.round(km)} km</span>}</span>
              </span>
            </button>
          ))}
          {!lijst.length && <p className="stil" style={{ padding: 12 }}>Geen vestiging gevonden.</p>}
        </div>
      )}
      <Lade titel={gekozen?.naam ?? ''} open={!!gekozen} sluit={() => kies(null)}
        kinderen={gekozen && <LocatieDetail v={gekozen} nu={nu} />} />
    </section>
  )
}

function LocatieDetail({ v, nu }: { v: Vestiging; nu: Date }) {
  const s = openStand(v.uren, nu)
  const vandaag = amsterdam(nu).dag
  const apple = isApple()
  const heeftAdres = !!(v.adres || (v.lat && v.lon))
  return (
    <div className="lijst" style={{ gap: 16 }}>
      <div className="rij"><OpenBadge s={s} /></div>
      {v.adres && (
        <div className="rij" style={{ alignItems: 'flex-start', flexWrap: 'nowrap' }}>
          <MapPin size={20} className="stil" style={{ marginTop: 2 }} />
          <span>{v.adres}<br />{v.postcode.replace(/^(\d{4})\s*([A-Za-z]{2})$/, '$1 $2').toUpperCase()} {v.plaats}</span>
        </div>
      )}
      {v.uren ? (
        <table className="uren-tabel">
          <tbody>
            {DAGEN.map((d) => {
              const vak = v.uren?.[d]
              return (
                <tr key={d} className={d === vandaag ? 'vandaag' : ''}>
                  <td>{DAGNAAM[d].charAt(0).toUpperCase() + DAGNAAM[d].slice(1)}</td>
                  <td>{vak ? `${vak.van} – ${vak.tot}` : 'Gesloten'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      ) : <div className="melding info">De openingstijden van deze vestiging volgen nog.</div>}
      {heeftAdres && (
        <div className="route-knoppen">
          <a className={`knop ${apple ? '' : 'geel'}`} href={googleRoute(v)} target="_blank" rel="noreferrer"><Navigation size={19} />Google Maps</a>
          <a className={`knop ${apple ? 'geel' : ''}`} href={appleRoute(v)} target="_blank" rel="noreferrer"><Navigation size={19} />Apple Kaarten</a>
        </div>
      )}
      {v.telefoon && <a className="knop breed" href={`tel:${v.telefoon.replace(/[^\d+]/g, '')}`}><Phone size={19} />Bel {v.telefoon}</a>}
    </div>
  )
}

